import React, { useMemo } from 'react';
import { format } from 'date-fns';
import {
  VictoryChart,
  VictoryLine,
  VictoryScatter,
  VictoryAxis,
  VictoryVoronoiContainer,
  VictoryCursorContainer,
  createContainer,
  VictoryBar,
  VictoryLabel,
  VictoryArea
} from 'victory';
import Box from '@amzn/awsui-components-react/polaris/box';
import Header from '@amzn/awsui-components-react/polaris/header';

import html2canvas from 'html2canvas';
import { Button } from '@amzn/awsui-components-react';

interface ForecastChartProps {
  data: any[];
  asin: string;
  title: string;
}

interface ForecastChartProps {
  data: any[];
  asin: string;
  title: string;
}


const ForecastChart: React.FC<ForecastChartProps> = ({ data, asin, title }) => {
  const chartData = useMemo(() => {
    return data.filter(d => d.asin === asin);
  }, [data, asin]);

  const sites = useMemo(() => {
    return Array.from(
      new Set(
        chartData
          .filter(d => d?.business_id && d?.site_id)
          .map(d => `${d.business_id} – ${d.site_id}`)
      )
    ).filter(site => site !== ' – ');
  }, [chartData]);

  const getDeduplicatedSiteData = (siteData) => {
    // Create a map to track unique data points by date
    const uniqueDataMap = new Map();
    
    // Process each data point
    siteData.forEach(dataPoint => {
      if (dataPoint.units) {
        const timestamp = new Date(dataPoint.order_day).getTime();
        const key = `${timestamp}-${dataPoint.units}`;
        
        // Only keep the first occurrence of each unique data point
        if (!uniqueDataMap.has(key)) {
          uniqueDataMap.set(key, dataPoint);
        }
      }
    });
    
    // Convert map values back to array
    return Array.from(uniqueDataMap.values());
  };
  

  const handleDownload = async (site: string) => {
    const chartElement = document.getElementById(`chart-${site}`);
    if (chartElement) {
      try {
        const canvas = await html2canvas(chartElement);
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `forecast-${asin}-${site.replace(/\s+/g, '-')}.png`;
        link.href = url;
        link.click();
      } catch (error) {
        console.error('Error downloading chart:', error);
      }
    }
  };
  // Create the combined container with proper typing
  const VoronoiCursorContainer = createContainer<
    React.ComponentProps<typeof VictoryVoronoiContainer>,
    React.ComponentProps<typeof VictoryCursorContainer>
  >("voronoi", "cursor");


  return (
    <Box padding="l">
      <Header variant="h1">{title}</Header>
      {sites.map(site => {
        // Calculate yMax for the site
        const siteData = chartData.filter(d => `${d.business_id} – ${d.site_id}` === site);
        const deduplicatedSiteData = getDeduplicatedSiteData(siteData);

        const yMax = Math.max(
          ...deduplicatedSiteData.map(d => Math.max(d.units || 0, d.p50 || 0, d.p90 || 0))
        ) * 1.2;

        return (
          <div key={site} style={{ marginBottom: '2rem' }}>
            {/* Download Button */}
            <Box textAlign="right" padding={{ bottom: 's' }}>
              <Button
                onClick={() => handleDownload(site)}
                iconName="download"
              >
                Download Chart
              </Button>
            </Box>

            {/* Chart Container with ID for download */}
            <div id={`chart-${site}`}>
              <VictoryChart
                width={2000}
                height={500}
                padding={{ top: 50, bottom: 50, left: 50, right: 50 }}
                scale={{ x: "time" }}
                domain={{ y: [0, yMax] }}
                containerComponent={
                   <VoronoiCursorContainer
                    voronoiBlacklist={["victory-label"]}
                    cursorComponent={
                      <line
                        style={{
                          stroke: "#808080",  // Light grey color
                          strokeWidth: 1
                      }}
                      />
                    }
                    labels={({ datum }) => {
                      if (!datum) return "";
                      const date = format(new Date(datum.x), 'MM/dd/yyyy');
                      
                      // For promo periods
                      if (datum.promoType) {
                        return `${datum.promoType}\nStart: ${format(new Date(datum.startDate), 'MM/dd/yyyy')}\nEnd: ${format(new Date(datum.endDate), 'MM/dd/yyyy')}`;
                      }
                      
                      // For actual data points (green dots)
                      if (datum.style && datum.style.data && datum.style.data.fill === "rgb(38, 217, 38)") {
                        // Find the actual data point in siteData to avoid duplicates
                        const actualPoint = siteData.find(d => 
                          d.units && new Date(d.order_day).getTime() === datum.x
                        );
                        
                        if (actualPoint) {
                          return `Date: ${date}\nActual Units: ${actualPoint.units.toFixed(2)}\nASIN: ${asin}\nSite: ${site.split(' – ')[1]}`;
                        }
                        return ''; // Skip duplicate tooltips
                      }
                      
                      // For forecast lines
                      if (datum.style && datum.style.data && datum.style.data.stroke) {
                        const modelType = datum.style.data.stroke === "firebrick" ? "Final" : 
                                        (datum.style.data.stroke === "steelblue" ? "FAST" : "Forecast");
                        const forecastType = datum.style.data.strokeDasharray ? "P90" : "P50";
                        return `Date: ${date}\n${modelType} ${forecastType}: ${datum.y.toFixed(2)}`;
                      }
                      
                      // Default case
                      return `Date: ${date}\nValue: ${datum.y.toFixed(2)}`;
                    }}
                  />
                }
              >
              {/* Promo/Markdown Rectangles */}
              {siteData
                .filter(d => d.promo_or_markdown === "Promo" || d.promo_or_markdown === "Markdown")
                .map((period, index) => (
                  <VictoryArea
                    key={`${period.promo_or_markdown}-${index}`}
                    data={[
                      { x: new Date(period.date_start).getTime() - 43200000, y: 0 },
                      { x: new Date(period.date_start).getTime() - 43200000, y: yMax },
                      { x: new Date(period.date_end).getTime() + 43200000, y: yMax },
                      { x: new Date(period.date_end).getTime() + 43200000, y: 0 }
                    ]}
                    style={{
                      data: {
                        fill: period.promo_or_markdown === "Promo" 
                          ? "rgba(128, 128, 128, 0.15)" 
                          : "rgba(128, 0, 128, 0.15)",
                        stroke: "none"
                      }
                    }}
                    // Add these props to make it work with tooltips
                    events={[{
                      target: "data",
                      eventHandlers: {
                        onMouseOver: () => {
                          return [{
                            target: "data",
                            mutation: () => ({
                              promoType: period.promo_or_markdown,
                              startDate: period.date_start,
                              endDate: period.date_end
                            })
                          }];
                        }
                      }
                    }]}
                  />
                ))}

                {/* Holiday Lines */}
                {siteData
                  .filter(d => d.is_holiday === 1)
                  .map((holiday, index) => (
                    <VictoryLine
                      key={`holiday-${index}`}
                      data={[
                        { x: new Date(holiday.holiday_date || holiday.order_day).getTime(), y: 0 },
                        { x: new Date(holiday.holiday_date || holiday.order_day).getTime(), y: yMax }
                      ]}
                      style={{
                        data: {
                          stroke: "red",
                          strokeWidth: 1,
                          strokeDasharray: "4,4,1,4"
                        }
                      }}
                    />
                ))}

                
                
                {/* Actual Points with Instock Color */}
               {/* Actual Points - Low Instock (Red) */}
                <VictoryScatter
                  name="actuals-low"
                  data={deduplicatedSiteData
                    .filter(d => d.units && (d.instock_raw || 0) <= 0.25)
                    .map(d => ({
                      x: new Date(d.order_day).getTime(),
                      y: d.units,
                      label: Math.round(d.units).toString()
                    }))}
                  size={6}
                  style={{
                    data: {
                      fill: "rgb(215, 48, 39)",  // Red
                      stroke: "black",
                      strokeWidth: 0.5
                    },
                    labels: {
                      fontSize: 10,
                      fill: "black",
                      padding: 5
                    }
                  }}
                  labelComponent={<VictoryLabel dy={-10} />}
                />

                {/* Actual Points - Medium-Low Instock (Orange) */}
                <VictoryScatter
                  name="actuals-medium-low"
                  data={deduplicatedSiteData
                    .filter(d => d.units && (d.instock_raw || 0) > 0.25 && (d.instock_raw || 0) <= 0.5)
                    .map(d => ({
                      x: new Date(d.order_day).getTime(),
                      y: d.units,
                      label: Math.round(d.units).toString()
                    }))}
                  size={6}
                  style={{
                    data: {
                      fill: "rgb(252, 141, 89)",  // Orange
                      stroke: "black",
                      strokeWidth: 0.5
                    },
                    labels: {
                      fontSize: 10,
                      fill: "black",
                      padding: 5
                    }
                  }}
                  labelComponent={<VictoryLabel dy={-10} />}
                />

                {/* Actual Points - Medium-High Instock (Light Green) */}
                <VictoryScatter
                  name="actuals-medium-high"
                  data={deduplicatedSiteData
                    .filter(d => d.units && (d.instock_raw || 0) > 0.5 && (d.instock_raw || 0) <= 0.75)
                    .map(d => ({
                      x: new Date(d.order_day).getTime(),
                      y: d.units,
                      label: Math.round(d.units).toString()
                    }))}
                  size={6}
                  style={{
                    data: {
                      fill: "rgb(145, 207, 96)",  // Light Green
                      stroke: "black",
                      strokeWidth: 0.5
                    },
                    labels: {
                      fontSize: 10,
                      fill: "black",
                      padding: 5
                    }
                  }}
                  labelComponent={<VictoryLabel dy={-10} />}
                />

                {/* Actual Points - High Instock (Dark Green) */}
                <VictoryScatter
                  name="actuals-high"
                  data={deduplicatedSiteData
                    .filter(d => d.units && (d.instock_raw || 0) > 0.75)
                    .map(d => ({
                      x: new Date(d.order_day).getTime(),
                      y: d.units,
                      label: Math.round(d.units).toString()
                    }))}
                  size={6}
                  style={{
                    data: {
                      fill: "rgb(26, 152, 80)",  // Dark Green
                      stroke: "black",
                      strokeWidth: 0.5
                    },
                    labels: {
                      fontSize: 10,
                      fill: "black",
                      padding: 5
                    }
                  }}
                  labelComponent={<VictoryLabel dy={-10} />}
                />


                {/* Final Forecast P50 Line */}
                <VictoryLine
                  data={siteData
                    .filter(d => d.model_version === "Final Forecast")
                    .map(d => ({
                      x: new Date(d.order_day).getTime(),
                      y: d.p50
                    }))}
                  style={{
                    data: { stroke: "firebrick", strokeWidth: 1 }
                  }}
                />

                {/* Final Forecast P90 Line */}
                <VictoryLine
                  data={siteData
                    .filter(d => d.model_version === "Final Forecast")
                    .map(d => ({
                      x: new Date(d.order_day).getTime(),
                      y: d.p90
                    }))}
                  style={{
                    data: { 
                      stroke: "firebrick", 
                      strokeWidth: 1,
                      strokeDasharray: "5,1"
                    }
                  }}
                />

                {/* Numpyro P50 Line */}
                <VictoryLine
                  data={siteData
                    .filter(d => d.model_version === "numpyro-promo-lifts-v1")
                    .map(d => ({
                      x: new Date(d.order_day).getTime(),
                      y: d.p50
                    }))}
                  style={{
                    data: { stroke: "steelblue", strokeWidth: 1 }
                  }}
                />

                {/* Numpyro P90 Line */}
                <VictoryLine
                  data={siteData
                    .filter(d => d.model_version === "numpyro-promo-lifts-v1")
                    .map(d => ({
                      x: new Date(d.order_day).getTime(),
                      y: d.p90
                    }))}
                  style={{
                    data: { 
                      stroke: "steelblue", 
                      strokeWidth: 1,
                      strokeDasharray: "5,1"
                    }
                  }}
                />

                <VictoryAxis
                  tickFormat={(t) => format(new Date(t), 'MM/dd/yy')}
                  label="Date"
                  tickCount={10}
                  tickValues={(() => {
                  // Get min and max dates from the data
                  const dates = siteData
                    .map(d => new Date(d.order_day).getTime())
                    .filter(d => !isNaN(d));
                  
                  const minDate = new Date(Math.min(...dates));
                  const maxDate = new Date(Math.max(...dates));
                  
                  // Generate ticks at 10-day intervals
                  const ticks:  number[] = [];
                  const currentDate = new Date(minDate);
                  while (currentDate <= maxDate) {
                    ticks.push(currentDate.getTime());
                    currentDate.setDate(currentDate.getDate() + 10); // Add 10 days
                  }
                  return ticks;
                })()}
                    style={{
                    tickLabels: { fontSize: 16, padding: 5 },
                    axis: { stroke: "#756f6a" },
                    grid: { stroke: "none" }
                  }}
                />

                <VictoryAxis
                  dependentAxis
                  label="Units"
                  style={{
                    tickLabels: { fontSize: 16, padding: 5 },
                    axis: { stroke: "#756f6a" },
                    grid: { stroke: "#A9A9A9", strokeWidth: 1, strokeDasharray: "5,5" }  // Add grid styling
                  }}
                />
              </VictoryChart>

              <Header variant="h2">{site}</Header>
            </div>
          </div>
        );
      })}

      <Box padding="s">
        <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
          <div>
            <span style={{ color: 'firebrick' }}>—</span> Final Forecast P50
            <span style={{ color: 'firebrick', marginLeft: '10px' }}>- - -</span> Final Forecast P90
          </div>
          <div>
            <span style={{ color: 'steelblue' }}>—</span> FAST P50
            <span style={{ color: 'steelblue', marginLeft: '10px' }}>- - -</span> FAST P90
          </div>
          <div>
            <span style={{ 
              display: 'inline-block', 
              width: '20px', 
              height: '10px', 
              backgroundColor: 'rgba(128, 128, 128, 0.15)', 
              marginRight: '5px' 
            }}></span>
            Promo
          </div>
          <div>
            <span style={{ 
              display: 'inline-block', 
              width: '20px', 
              height: '10px', 
              backgroundColor: 'rgba(128, 0, 128, 0.15)', 
              marginRight: '5px' 
            }}></span>
            Markdown
          </div>
          <div>
            <span style={{ color: 'red', borderBottom: '1px dashed red' }}>|</span>
            Holiday
          </div>
          {/* ASIN Actual with In-Stock Colors */}
          {/* Instock Color Legend */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span>Instock: </span>
              <span style={{ fontSize: '10px', marginLeft: '5px' }}>0%</span>
              <div style={{ 
                display: 'flex', 
                width: '100px', 
                height: '10px', 
                marginLeft: '5px',
                background: 'linear-gradient(to right, rgb(215, 48, 39), rgb(252, 141, 89), rgb(145, 207, 96), rgb(26, 152, 80))'
              }}></div>
              <span style={{ fontSize: '10px', marginLeft: '5px' }}>100%</span>
            </div>
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default ForecastChart;
